import type { EpisodeOfCare } from '@medplum/fhirtypes';
import {
  isCalendarClientAttendanceAbsent,
  isCalendarClientAttendancePresent,
  normalizeCalendarClientAttendanceStatus,
} from '@/lib/calendarClientAttendanceStatus';

export type FamilyTherapyParticipantAttendance = {
  identifiedPatientId?: string;
  attendanceByPatientId: Record<string, string | undefined>;
  participantPatientIds: string[];
};

export function resolveIdentifiedPatientId(input: {
  episodeOfCare?: EpisodeOfCare | null;
  notePatientRef?: string;
  participantPatientIds: string[];
}): string | undefined {
  const fromEoc = input.episodeOfCare?.patient?.reference?.replace(/^Patient\//, '').trim();
  if (fromEoc) return fromEoc;
  const fromNote = input.notePatientRef?.replace(/^Patient\//, '').trim();
  if (fromNote) return fromNote;
  return input.participantPatientIds[0];
}

function normalizedStatus(
  attendanceByPatientId: Record<string, string | undefined> | undefined,
  patientId: string,
) {
  return normalizeCalendarClientAttendanceStatus(attendanceByPatientId?.[patientId]);
}

/**
 * Derive family psychotherapy scrubber flags from Participant Attendance rows.
 *
 * 90847 — identified patient present; 90846 — family present without identified patient.
 */
export function deriveFamilyTherapyAttendanceFromParticipants(
  input: FamilyTherapyParticipantAttendance | undefined,
): {
  identifiedPatientPresent?: boolean;
  familyParticipantPresent?: boolean;
} {
  if (!input || !input.participantPatientIds?.length) {
    return { identifiedPatientPresent: undefined, familyParticipantPresent: undefined };
  }

  const attendanceByPatientId = input.attendanceByPatientId ?? {};
  const identifiedPatientId =
    input.identifiedPatientId ?? input.participantPatientIds[0];

  let identifiedPatientPresent: boolean | undefined;
  if (identifiedPatientId) {
    const status = normalizedStatus(attendanceByPatientId, identifiedPatientId);
    if (status && isCalendarClientAttendancePresent(status)) {
      identifiedPatientPresent = true;
    } else if (status && isCalendarClientAttendanceAbsent(status)) {
      identifiedPatientPresent = false;
    }
  }

  const familyIds = input.participantPatientIds.filter((id) => id !== identifiedPatientId);
  let familyParticipantPresent: boolean | undefined;
  if (familyIds.length > 0) {
    const familyStatuses = familyIds
      .map((id) => normalizedStatus(attendanceByPatientId, id))
      .filter((s): s is NonNullable<typeof s> => s != null);

    if (familyStatuses.some(isCalendarClientAttendancePresent)) {
      familyParticipantPresent = true;
    } else if (
      familyIds.every((id) => {
        const status = normalizedStatus(attendanceByPatientId, id);
        return status != null && isCalendarClientAttendanceAbsent(status);
      })
    ) {
      familyParticipantPresent = false;
    } else if (
      familyIds.every((id) => {
        const status = normalizedStatus(attendanceByPatientId, id);
        return status != null;
      }) &&
      !familyStatuses.some(isCalendarClientAttendancePresent)
    ) {
      familyParticipantPresent = false;
    }
  }

  return { identifiedPatientPresent, familyParticipantPresent };
}

/**
 * Derive 90853 group-encounter attendance from Participant Attendance.
 * Prefers the identified / note patient; otherwise any present / all absent.
 */
export function deriveGroupEncounterAttendanceFromParticipants(
  input: FamilyTherapyParticipantAttendance | undefined,
): boolean | undefined {
  if (!input?.participantPatientIds?.length) return undefined;

  const attendanceByPatientId = input.attendanceByPatientId ?? {};
  const identifiedPatientId = input.identifiedPatientId ?? input.participantPatientIds[0];

  if (identifiedPatientId) {
    const status = normalizedStatus(attendanceByPatientId, identifiedPatientId);
    if (status && isCalendarClientAttendancePresent(status)) return true;
    if (status && isCalendarClientAttendanceAbsent(status)) return false;
  }

  const statuses = input.participantPatientIds
    .map((id) => normalizedStatus(attendanceByPatientId, id))
    .filter((s): s is NonNullable<typeof s> => s != null);

  if (statuses.some(isCalendarClientAttendancePresent)) return true;
  if (
    input.participantPatientIds.length > 0 &&
    input.participantPatientIds.every((id) => {
      const status = normalizedStatus(attendanceByPatientId, id);
      return status != null && isCalendarClientAttendanceAbsent(status);
    })
  ) {
    return false;
  }
  return undefined;
}

